import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NoticeCard from '../../components/cards/NoticeCard';

const NoticeDetail = () => {
  const { id } = useParams();
  const { user } = useAuth();
  const [notice, setNotice] = useState(null);
  const [relatedNotices, setRelatedNotices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchNotice = async () => {
      try {
        // In a real app, this would be an API call
        // const response = await axios.get(`/api/notices/${id}`);
        
        // Mock data for now
        const mockNotice = {
          _id: id,
          title: 'University Annual Examination Schedule Released',
          description: `The annual examination schedule for the current academic year has been officially released. All students are required to check their respective schedules carefully and prepare accordingly. The examinations will commence from March 15th, 2024, and continue for the next six weeks. Students are advised to reach the examination hall at least 15 minutes before the commencement of each paper. 

          Important Instructions:
          - Bring valid student ID card
          - Carry black/blue ballpoint pens
          - No electronic devices allowed
          - Follow examination hall discipline
          
          Any discrepancies in the schedule should be reported to the examination office immediately. Students with special needs or health concerns should contact the disability support office at least one week before the examination date.`,
          category: 'Exam',
          priority: 'Important',
          publishedBy: {
            name: 'Dr. Sarah Johnson',
            email: 'sarah.johnson@university.edu'
          },
          createdAt: '2024-01-15T10:30:00Z',
          views: 1250,
          fileUrl: '/uploads/sample-notice.pdf'
        };

        setNotice(mockNotice);
        
        // Mock related notices
        const mockRelatedNotices = [
          {
            _id: 'rel1',
            title: 'Supplementary Exam Registration Open',
            description: 'Registration for supplementary exams is now open for eligible students.',
            category: 'Exam',
            priority: 'Normal',
            publishedBy: { name: 'Exam Office' },
            createdAt: '2024-01-10T09:15:00Z',
            views: 890
          },
          {
            _id: 'rel2',
            title: 'Library Extended Hours During Exams',
            description: 'Library will remain open 24/7 during the examination period.',
            category: 'General',
            priority: 'Normal',
            publishedBy: { name: 'Library Dept' },
            createdAt: '2024-01-12T14:20:00Z',
            views: 756
          },
          {
            _id: 'rel3',
            title: 'Exam Center Assignments Published',
            description: 'Check your assigned exam centers and seat numbers.',
            category: 'Exam',
            priority: 'Important',
            publishedBy: { name: 'Exam Office' },
            createdAt: '2024-01-14T11:45:00Z',
            views: 1023
          }
        ];
        
        setRelatedNotices(mockRelatedNotices);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching notice:', error);
        setLoading(false);
      }
    };

    fetchNotice();
  }, [id]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl">Loading...</div>
      </div>
    );
  }

  if (!notice) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-2xl text-red-600">Notice not found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-4">
        <Link 
          to="/notices" 
          className="inline-flex items-center text-primary hover:underline mb-6"
        >
          ← Back to Notices
        </Link>

        <article className="bg-white rounded-lg shadow-md p-8 mb-8">
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-3xl font-bold text-gray-800 mb-4">{notice.title}</h1>
              
              <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-4">
                <span className="flex items-center">
                  <span className="font-medium mr-2">Published by:</span>
                  {notice.publishedBy?.name || 'Admin'}
                </span>
                
                <span className="flex items-center">
                  <span className="font-medium mr-2">Date:</span>
                  {formatDate(notice.createdAt)}
                </span>
                
                <span className="flex items-center">
                  <span className="font-medium mr-2">Views:</span>
                  {notice.views}
                </span>
              </div>
            </div>
            
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${
              notice.priority === 'Urgent' 
                ? 'bg-red-100 text-red-800' 
                : notice.priority === 'Important' 
                  ? 'bg-yellow-100 text-yellow-800' 
                  : 'bg-green-100 text-green-800'
            }`}>
              {notice.priority}
            </span>
          </div>

          <div className="mb-6">
            <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
              {notice.category}
            </span>
          </div>

          <div className="prose max-w-none text-gray-700 mb-8">
            {notice.description.split('\n\n').map((paragraph, index) => (
              <p key={index} className="mb-4">
                {paragraph.trim()}
              </p>
            ))}
          </div>

          {notice.fileUrl && (
            <div className="mt-8 pt-6 border-t">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Attachments</h3>
              <div className="flex items-center p-4 bg-gray-50 rounded-lg">
                <span className="text-2xl mr-3">📄</span>
                <div>
                  <p className="font-medium">Download Notice Document</p>
                  <a 
                    href={notice.fileUrl} 
                    download
                    className="text-primary hover:underline text-sm"
                  >
                    {notice.fileUrl.split('/').pop() || 'notice-document.pdf'}
                  </a>
                </div>
              </div>
            </div>
          )}
        </article>

        {relatedNotices.length > 0 && (
          <section>
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Related Notices</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedNotices.map((relatedNotice) => (
                <NoticeCard 
                  key={relatedNotice._id} 
                  notice={relatedNotice} 
                  showViewMore={true} 
                />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default NoticeDetail;